// src/components/accueil/CarteElementsSuivis.tsx — SCE Issues
// « Éléments suivis » de l'accueil (Lot 12 ter), commune au coordinateur et à
// l'opérateur.
//
// Avant : « Problèmes suivis », sous-titrée « Projets suivis : PN4334 · … »,
// absente tant qu'aucun problème actif ne tombait dedans — on ne voyait ni
// ce qu'on suivait, ni pourquoi tel problème était là. Maintenant :
//  - le sous-titre dit ce qu'on suit : « 3 problèmes · 2 équipements · 1 projet » ;
//  - chaque problème dit pourquoi il est là : rien s'il est suivi pour
//    lui-même (cloche de la fiche), « via 4825-SC » ou « via PN4334 » ;
//  - « Nouveau » sur ceux où quelqu'un d'autre a agi depuis la dernière
//    visite, avec ce qu'il a fait (au bureau) ;
//  - les problèmes terminés (suivis un par un) sous un repère « Terminés ».
//
// Les pastilles « Vous suivez » (un clic = filtre sur un élément) ont été
// retirées le 30/09 : elles prenaient une rangée de la carte pour peu d'usage.
//
// « Par machine » (02/10) : comme la file et l'activité, un titre par N° de
// série ; le repère « Terminés » disparaît dans ce mode (chaque groupe garde
// ses actifs d'abord).

import { useMemo, useState } from 'react'
import type { ListeProblemes } from '@/components/dashboard/ListeProblemesSheet'
import { SuiteListe } from '@/components/layout/SuiteProblemes'
import { estActif, type Issue } from '@/types/issue.types'
import { estAbonne } from '@/utils/abonnements'
import { grouperParMachine, libelleAge, verbeEvenement, type EvenementAccueil } from '@/utils/accueil'
import { motifSuivi, nombreSuivis, type ElementsSuivis, type MotifSuivi } from '@/utils/elementsSuivis'
import { prenom } from '@/utils/noms'
import { CarteAccueil, PiedCarte, SqueletteLignes, VideCarte } from './CarteAccueil'
import { LigneProbleme } from './LigneProbleme'
import { EtatProbleme } from '@/components/ui/EtatProbleme'
import { usePreferenceLocale } from '@/hooks/usePreferenceLocale'
import { BoutonParMachine, pluriel, TitreGroupeMachine } from './GroupeMachine'

const LIGNES_TELEPHONE = 5

/** Faut-il montrer la carte quand elle n'est pas toujours là ? */
export function aDesSuivis(suivis: readonly Issue[], elements: ElementsSuivis): boolean {
  return suivis.length > 0 || nombreSuivis(elements) > 0
}

export function CarteElementsSuivis({
  issues,
  suivis,
  elements,
  nouveautes,
  monId,
  upn,
  maintenant,
  loading,
  large,
  ajuste,
  ouvrirListe,
}: {
  issues: readonly Issue[]
  /** `problemesSuivis(…)`, déjà triés. */
  suivis: Issue[]
  elements: ElementsSuivis
  /** Par problème, ce qu'un autre a fait depuis la dernière visite (`nouveautesParProbleme`). */
  nouveautes: ReadonlyMap<string, EvenementAccueil>
  monId: number | null
  upn: string
  maintenant: number
  loading: boolean
  /** Bureau : toutes les lignes, la carte défile ; téléphone : cinq, puis « Voir les N ». */
  large: boolean
  ajuste?: boolean
  ouvrirListe: (liste: ListeProblemes) => void
}): JSX.Element {
  const motifs = useMemo(
    () => new Map(suivis.map((i) => [i.id, motifSuivi(i, estAbonne(i.abonnes, monId, upn), elements)])),
    [suivis, monId, upn, elements],
  )
  const [parMachine, setParMachine] = usePreferenceLocale('sce-issues.accueil.suivis-par-machine', false)
  const [repliees, setRepliees] = useState<ReadonlySet<string>>(new Set())
  const basculer = (cle: string): void =>
    setRepliees((p) => {
      const n = new Set(p)
      if (n.has(cle)) n.delete(cle)
      else n.add(cle)
      return n
    })
  const visibles = large ? suivis : suivis.slice(0, LIGNES_TELEPHONE)
  const premierTermine = visibles.findIndex((i) => !estActif(i))
  const liste = (): void => ouvrirListe({ titre: 'Problèmes suivis', issues: suivis })
  const pied = large
    ? suivis.length > 0 ? <PiedCarte libelle="Ouvrir en liste" onClick={liste} /> : undefined
    : suivis.length > LIGNES_TELEPHONE ? <PiedCarte libelle={`Voir les ${suivis.length}`} onClick={liste} /> : undefined

  let lignes: JSX.Element
  if (loading && issues.length === 0) {
    lignes = <SqueletteLignes n={2} />
  } else if (suivis.length === 0) {
    lignes = (
      <VideCarte>
        {nombreSuivis(elements) > 0
          ? 'Aucun problème actif sur vos équipements et projets suivis.'
          : 'Rien de suivi. La cloche d’une fiche problème, équipement ou projet permet d’en suivre.'}
      </VideCarte>
    )
  } else if (parMachine) {
    const groupes = grouperParMachine(visibles, (i) => i)
    lignes = (
      <SuiteListe libelle="Éléments suivis" issues={groupes.flatMap((g) => (repliees.has(g.cle) ? [] : g.items))}>
        {groupes.map((g) => (
          <div key={g.cle}>
            <TitreGroupeMachine
              cle={g.cle}
              pn={g.pn}
              detail={pluriel(g.items.length, 'problème')}
              replie={repliees.has(g.cle)}
              nouveau={g.items.some((i) => nouveautes.has(i.id))}
              onBascule={() => basculer(g.cle)}
            />
            {!repliees.has(g.cle) &&
              g.items.map((i) => {
                const nouveau = nouveautes.get(i.id)
                return (
                  <LigneProbleme
                    key={i.id}
                    issue={i}
                    deuxLignes={!large}
                    meta={metaSuivi(i, motifs.get(i.id) ?? null, large ? nouveau : undefined, true)}
                    droite={
                      <>
                        {nouveau && <PastilleNouveau evenement={nouveau} maintenant={maintenant} />}
                        <EtatProbleme statut={i.statut} criticite={i.criticite} />
                      </>
                    }
                  />
                )
              })}
          </div>
        ))}
      </SuiteListe>
    )
  } else {
    lignes = (
      <SuiteListe libelle="Éléments suivis" issues={suivis}>
        {visibles.map((i, rang) => {
          const nouveau = nouveautes.get(i.id)
          return (
            <div key={i.id}>
              {rang === premierTermine && <RepereTermines />}
              <LigneProbleme
                issue={i}
                deuxLignes={!large}
                meta={metaSuivi(i, motifs.get(i.id) ?? null, large ? nouveau : undefined)}
                droite={
                  <>
                    {nouveau && <PastilleNouveau evenement={nouveau} maintenant={maintenant} />}
                    <EtatProbleme statut={i.statut} criticite={i.criticite} />
                  </>
                }
              />
            </div>
          )
        })}
      </SuiteListe>
    )
  }

  return (
    <CarteAccueil
      ajuste={ajuste}
      titre="Éléments suivis"
      sousTitre={resume(suivis.length, elements, nouveautes.size)}
      action={suivis.length > 0 ? <BoutonParMachine actif={parMachine} onChange={setParMachine} compact /> : undefined}
      pied={pied}
    >
      {lignes}
    </CarteAccueil>
  )
}

// ─── Morceaux ───

function RepereTermines(): JSX.Element {
  return (
    <div
      style={{
        padding: '6px 18px',
        fontSize: 12,
        color: 'var(--color-text-secondary)',
        backgroundColor: 'var(--color-gray-5)',
        borderBottom: '1px solid var(--color-gray-10)',
      }}
    >
      Terminés
    </div>
  )
}

/** Quelqu'un d'autre a agi depuis la dernière visite ; le détail est dans l'infobulle. */
function PastilleNouveau({ evenement, maintenant }: { evenement: EvenementAccueil; maintenant: number }): JSX.Element {
  const detail = `${prenom(evenement.evenement.auteur) || 'Quelqu’un'} ${verbeEvenement(evenement.evenement, false)} · ${libelleAge(evenement.evenement.date, maintenant)}`
  return (
    <span
      title={detail}
      aria-label={`Nouveau : ${detail}`}
      style={{
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 6px',
        borderRadius: 4,
        whiteSpace: 'nowrap',
        color: 'var(--color-white)',
        backgroundColor: 'var(--color-black)',
      }}
    >
      Nouveau
    </span>
  )
}

/**
 * « P2026-0042 · 4825-SC », « … · via 4825-SC », « … · via PN4334 ».
 * Au bureau, un problème avec du nouveau dit aussi ce qui s'est passé :
 * « … · Karim a commenté ».
 */
function metaSuivi(
  i: Issue,
  motif: MotifSuivi | null,
  nouveau: EvenementAccueil | undefined,
  /** Regroupé par machine : le N° de série est déjà dans le titre du groupe. */
  sousSaMachine = false,
): string {
  const quoi = nouveau
    ? `${prenom(nouveau.evenement.auteur) || 'Quelqu’un'} ${verbeEvenement(nouveau.evenement, false)}`
    : ''
  const machine = sousSaMachine ? '' : i.numeroSerie
  switch (motif?.genre) {
    case 'equipement':
      return [i.id, sousSaMachine ? 'via l’équipement' : `via ${i.numeroSerie}`, quoi].filter(Boolean).join(' · ')
    case 'projet':
      return [i.id, machine, `via ${motif.pn}`, quoi].filter(Boolean).join(' · ')
    default:
      return [i.id, machine, quoi].filter(Boolean).join(' · ')
  }
}

/** « 3 problèmes · 2 équipements · 1 projet · 2 avec du nouveau » — ce qui est vide est omis. */
function resume(problemes: number, elements: ElementsSuivis, avecNouveau: number): string | undefined {
  const morceau = (n: number, mot: string): string => (n > 0 ? `${n} ${mot}${n > 1 ? 's' : ''}` : '')
  const texte = [
    morceau(problemes, 'problème'),
    morceau(elements.equipements.length, 'équipement'),
    morceau(elements.projets.length, 'projet'),
    avecNouveau > 0 ? `${avecNouveau} avec du nouveau` : '',
  ].filter(Boolean).join(' · ')
  return texte || undefined
}
